// ideally this would go in theme component override,
// but Alert is used in lift-remix

import {
  Alert as MuiAlert,
  Typography,
  useTheme,
  svgIconClasses,
} from '@mui/material';
import type { AlertProps as MuiAlertProps } from '@mui/material';

import { Checkmark, TechnicalWarning } from '@tbd/life-jsx/SystemIcons';
import { COLORS } from '@tbd/life-tokens';
import { TextButton } from '@tbd/lift-ui/components/TextButton';

const DEFAULT_SX = {};

const WARNING_SX = {
  color: COLORS.warning_700,
  borderColor: COLORS.accent2_200,
  backgroundColor: COLORS.warning_20,
} as const;

const ERROR_SX = {
  color: COLORS.critical_700,
  borderColor: COLORS.critical_200,
  backgroundColor: COLORS.critical_20,
} as const;

const SUCCESS_SX = {
  color: COLORS.correct_700,
  borderColor: COLORS.correct_200,
  backgroundColor: COLORS.correct_20,
} as const;

const PALETTE_MAP = {
  warning: WARNING_SX,
  error: ERROR_SX,
  success: SUCCESS_SX,
} as const;

export type LiftPortalAlertSeverity = Exclude<
  NonNullable<MuiAlertProps['severity']>,
  'info'
>;

export type LiftPortalAlertProps = Omit<
  MuiAlertProps,
  'severity' | 'children' | 'action'
> & {
  severity: LiftPortalAlertSeverity;
  message: string;
  action?: {
    text: string;
    onClick: () => void;
  };
};

export function LiftPortalAlert(props: LiftPortalAlertProps) {
  const severityPalette = PALETTE_MAP[props.severity];
  const { action, sx = DEFAULT_SX, ...alertProps } = props;
  return (
    <MuiAlert
      action={
        action ? (
          <AlertActionButton {...action} color={severityPalette.color} />
        ) : null
      }
      iconMapping={{
        warning: props.icon ?? <TechnicalWarning />,
        success: props.icon ?? <Checkmark />,
      }}
      slotProps={{
        root: {
          sx: {
            border: `1px solid ${severityPalette.borderColor}`,
            background: severityPalette.backgroundColor,
            ...sx,
          },
        },
        icon: {
          sx: {
            [`& .${svgIconClasses.root}`]: {
              color: severityPalette.color,
              width: '20px',
              height: '20px',
            },
          },
        },
        action: {
          sx: {
            padding: 0,
            alignItems: 'center',
          },
        },
      }}
      {...alertProps}
    >
      <Typography
        variant="component-regular-xs"
        sx={{ color: severityPalette.color }}
      >
        {props.message}
      </Typography>
    </MuiAlert>
  );
}

function AlertActionButton(props: {
  text: string;
  onClick: () => void;
  color: (typeof PALETTE_MAP)[LiftPortalAlertSeverity]['color'];
}) {
  const theme = useTheme();

  return (
    <TextButton
      variant="tertiary"
      text={props.text}
      onClick={props.onClick}
      sx={{
        padding: 0,
        height: '16px',
        color: props.color,
        '&:hover': {
          color: props.color,
        },
        ...theme.typography['component-bold-xs'],
      }}
    />
  );
}
